import { useMemo } from "react";

export type TrialTone = "success" | "warning" | "critical";

export interface TrialStatus {
  /** Whole days remaining until expiry (clamped to >= 0). */
  daysLeft: number;
  /** Visual urgency bucket derived from {@link daysLeft}. */
  tone: TrialTone;
  /** True when the expiry date is already in the past. */
  isExpired: boolean;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** More days left → calmer color; fewer → more urgent. */
export function getTrialTone(daysLeft: number): TrialTone {
  if (daysLeft > 15) return "success";
  if (daysLeft > 5) return "warning";
  return "critical";
}

/**
 * Computes trial/expiry status from real tenant data.
 * Returns null when the license type is not "trial" or "paid" (e.g. "internal"),
 * which hides the chip in the header.
 */
export function useTrialStatus(
  expiresAtMs: number | null | undefined,
  licenseType: string | null | undefined,
): TrialStatus | null {
  return useMemo(() => {
    if (licenseType !== "trial" && licenseType !== "paid") return null;
    if (expiresAtMs == null) return null;

    const now = Date.now();
    const daysLeft = Math.max(0, Math.ceil((expiresAtMs - now) / MS_PER_DAY));
    return {
      daysLeft,
      tone: getTrialTone(daysLeft),
      isExpired: expiresAtMs < now,
    };
  }, [expiresAtMs, licenseType]);
}
